import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { productsApi } from '@/api/products.api';
import { queryKeys } from '@/hooks/queryKeys';
import { useUIStore } from '@/store/uiStore';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PriceDisplay } from '@/components/ui/PriceDisplay';
import { ProductImageGallery } from './ProductImageGallery';
import { ProductVariantSelector } from './ProductVariantSelector';
import { useAddToCart } from '@/hooks/useCart';
import { useCartStore } from '@/store/cartStore';
import { effectivePricePaise } from '@/types/product';

export function QuickViewModal() {
  const isOpen = useUIStore((s) => s.isQuickViewOpen);
  const slug = useUIStore((s) => s.quickViewProductSlug);
  const close = useUIStore((s) => s.closeQuickView);

  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const isPending = useCartStore((s) =>
    selectedVariantId ? s.isPending(selectedVariantId) : false
  );

  const { data: product, isLoading } = useQuery({
    queryKey: slug ? queryKeys.products.detail(slug) : ['quick-view-empty'],
    queryFn: () => productsApi.getBySlug(slug!),
    enabled: !!slug && isOpen,
    staleTime: 5 * 60_000,
  });

  const addToCart = useAddToCart();

  useEffect(() => {
    if (product?.variants?.[0]) {
      setSelectedVariantId(product.variants[0].id);
    }
  }, [product?.id, product?.variants]);

  const variant = product?.variants?.find((v) => v.id === selectedVariantId);
  const price = product
    ? effectivePricePaise(product, variant ?? undefined)
    : 0;

  const handleAdd = () => {
    if (!selectedVariantId) return;
    addToCart.mutate({ variantId: selectedVariantId, quantity: 1 });
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="xl" hideCloseButton={false}>
      {isLoading || !product ? (
        <div className="flex min-h-[420px] items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="grid gap-8 md:grid-cols-2">
          <ProductImageGallery
            images={product.images ?? []}
            productName={product.name}
          />
          <div className="flex flex-col gap-5">
            {product.category && (
              <p className="text-label-mono text-[color:var(--color-text-muted)]">
                {product.category.name}
              </p>
            )}
            <h2 className="font-[family-name:var(--font-editorial)] text-3xl text-white">
              {product.name}
            </h2>
            <PriceDisplay
              paise={price}
              originalPaise={
                product.discountedPrice && product.discountedPrice < product.basePrice
                  ? product.basePrice
                  : null
              }
              gstRate={product.gstRate}
              size="lg"
            />
            {product.description && (
              <p className="line-clamp-4 text-[14px] leading-relaxed text-[color:var(--color-text-secondary)]">
                {product.description}
              </p>
            )}

            <ProductVariantSelector
              variants={product.variants ?? []}
              selectedVariantId={selectedVariantId}
              onSelect={setSelectedVariantId}
            />

            <div className="mt-2 flex flex-col gap-3">
              <Button
                onClick={handleAdd}
                isLoading={isPending}
                disabled={!selectedVariantId}
                block
              >
                Add to Cart
              </Button>
              <Button variant="secondary" onClick={close} block>
                View Full Details
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
